import { createEnv } from "@offstreetparking/env-validator";
import * as z from "zod/v4";

export const env = createEnv({
	clientPrefix: "NEXT_PUBLIC_",
	/**
	 * Specify your server-side environment variables schema here. This way you can ensure the app
	 * isn't built with invalid env vars.
	 */
	server: {
		PORTAL_V2_URL: z.url(),
		NODE_ENV: z.enum(["development", "test", "production"]),
		INTERNAL_API_URL: z.url().optional(),
		WORKOS_API_KEY: z.string(),
		WORKOS_API_HOSTNAME: z.string().optional(),
		WORKOS_API_HTTPS: z
			.enum(["true", "false"])
			.optional()
			.transform((v) => (v == null ? undefined : v === "true")),
		WORKOS_API_PORT: z.coerce.number().int().positive().optional(),
		WORKOS_CLIENT_ID: z.string(),
		WORKOS_COOKIE_NAME: z.string(),
		WORKOS_COOKIE_PASSWORD: z.string(),
		CORE_API_KEY: z.string().optional(),
	},

	/**
	 * Specify your client-side environment variables schema here. This way you can ensure the app
	 * isn't built with invalid env vars. To expose them to the client, prefix them with
	 * `NEXT_PUBLIC_`.
	 */
	client: {
		NEXT_PUBLIC_API_URL: z.string(),
		NEXT_PUBLIC_POSTHOG_KEY: z.string(),
		NEXT_PUBLIC_POSTHOG_HOST: z.string(),
		NEXT_PUBLIC_ENABLE_STATUSPAGE: z
			.enum(["true", "false"])
			.optional()
			.transform((v) => v === "true"),
		NEXT_PUBLIC_ENVIRO: z.string().optional(),
		NEXT_PUBLIC_SERVICE: z.string().default("portal-v3"),
		NEXT_PUBLIC_DATADOG_APPLICATION_ID: z.string().min(1),
		NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: z.string().min(1),
		NEXT_PUBLIC_COMMIT_SHA: z.string().optional(),
		NEXT_PUBLIC_APP_DOMAIN: z.string(),
	},

	/**
	 * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
	 * middlewares) or client-side so we need to destruct manually.
	 */
	runtimeEnv: {
		PORTAL_V2_URL: process.env.PORTAL_V2_URL,
		NODE_ENV: process.env.NODE_ENV,
		INTERNAL_API_URL: process.env.INTERNAL_API_URL,
		WORKOS_API_KEY: process.env.WORKOS_API_KEY,
		WORKOS_API_HOSTNAME: process.env.WORKOS_API_HOSTNAME,
		WORKOS_API_HTTPS: process.env.WORKOS_API_HTTPS,
		WORKOS_API_PORT: process.env.WORKOS_API_PORT,
		WORKOS_CLIENT_ID: process.env.WORKOS_CLIENT_ID,
		WORKOS_COOKIE_NAME: process.env.WORKOS_COOKIE_NAME,
		WORKOS_COOKIE_PASSWORD: process.env.WORKOS_COOKIE_PASSWORD,
		CORE_API_KEY: process.env.CORE_API_KEY,
		NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
		NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
		NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
		NEXT_PUBLIC_ENABLE_STATUSPAGE: process.env.NEXT_PUBLIC_ENABLE_STATUSPAGE,
		NEXT_PUBLIC_ENVIRO: process.env.NEXT_PUBLIC_ENVIRO,
		NEXT_PUBLIC_SERVICE: process.env.NEXT_PUBLIC_SERVICE,
		NEXT_PUBLIC_DATADOG_APPLICATION_ID: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
		NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
		NEXT_PUBLIC_COMMIT_SHA: process.env.NEXT_PUBLIC_COMMIT_SHA,
		NEXT_PUBLIC_APP_DOMAIN: process.env.NEXT_PUBLIC_APP_DOMAIN,
	},
	/**
	 * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
	 * useful for Docker builds.
	 */
	skipValidation: !!process.env.SKIP_ENV_VALIDATION,
	/**
	 * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
	 * `SOME_VAR=''` will throw an error.
	 */
	emptyStringAsUndefined: true,
});
